/**
 * Brand hex mirrors for Law 5 contexts where CSS variables cannot resolve
 * (email HTML, canvas, confetti, QR, Recharts/Stripe literal-string props).
 * Values must match styles/tokens.css + styles/themes/schoolconferencego.css
 * — Go Family Bible v2.0, Part 4 (SchoolConferenceGo · Royal).
 */
export const ACCENT_HEX = '#1054CD';       // --scg-key
export const ACCENT_DARK_HEX = '#093A92';  // --scg-hover
export const ACCENT_TINT_HEX = '#E8EEF7';  // --scg-tint

export const SCG = {
  tint: '#E8EEF7',
  wash: '#CCD9F0',
  lift: '#588BE4',
  key: '#1054CD',
  hover: '#093A92',
  deep: '#092453',
  ink: '#181D25',
};

export const GO = {
  bg: '#F3F3F3',
  warning: '#B45309',
  caution: '#D0021B',
  curtain: '#1B2233',
  gilt: '#E0AC3A',
};

export const COLORS = {
  paper: '#FFFFFF',
  wash: GO.bg,
  line: '#DADFE8',
  muted: '#5C6370',
  ink: SCG.ink,
  sizzleOrange: '#E26507',
  sizzleOrangeDark: '#C35502',
  sizzleOrangeTint: '#FEECE3',
  sizzleYellow: '#FFCB00',
  bannerInk: '#02042B',
  curtain: GO.curtain,
  curtainDark: '#0F141F',
  success: '#097E71',
  warning: GO.warning,
  danger: GO.caution,
  info: SCG.key,
  accent: ACCENT_HEX,
  accentDark: ACCENT_DARK_HEX,
  accentTint: ACCENT_TINT_HEX,
  white: '#FFFFFF',
  gilt: GO.gilt,
};

export const FONTS = {
  display: "'Capriola', sans-serif",
  ui: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
};

/** Suite product accents for app-switcher dots (Part 4) — identity color, not SCG action chrome. */
export const PRODUCT_ACCENTS = {
  signupgo: '#CC1962',
  schoolconferencego: ACCENT_HEX,
  eventticketgo: '#097E71',
  sizzle: '#E26507',
};

/** Lincoln demo school (/000000) theme — homepage phone mockup only. */
export const DEMO_THEME_HEX = '#2563EB';
export const DEMO_THEME_DARK_HEX = '#1D4ED8';

/** Default theme color offered to a school that hasn't picked its own (Part 4 exception —
 * user-configurable brand data, not a design token; centralized here per Law 5's
 * three-file rule so it isn't duplicated as a literal across route files). */
export const DEFAULT_SCHOOL_THEME_HEX = '#26415E';
export const DEFAULT_SCHOOL_THEME_DARK_HEX = '#0B1B54';

/** Categorical palette for teacher/team grouping chips — distinguishable, not brand-semantic. */
export const TEAM_COLOR_PALETTE = [
  '#22C55E',
  '#3B82F6',
  '#A855F7',
  '#F59E0B',
  '#D4D4D8',
  '#2563EB',
  '#6366F1',
];

/** Default teacher category set offered before a school customizes its own —
 * kept in one place so the admin dashboard and the parent-facing school page
 * never drift out of sync (Law 5's three-file rule). */
export const DEFAULT_TEACHER_CATEGORIES = [
  { id: 'k5', name: 'K-5', color: '#22C55E', order: 0 },
  { id: '6-8', name: '6-8', color: '#3B82F6', order: 1 },
  { id: 'specials', name: 'Specials', color: '#A855F7', order: 2 },
  { id: 'support', name: 'Support', color: '#F59E0B', order: 3 },
];

export const NEUTRAL_SWATCH_HEX = '#D4D4D8';
export const NEW_TEAM_COLOR_HEX = '#2563EB';
export const NEW_GROUP_COLOR_HEX = '#6366F1';

/** Internal ops-console announcement banner defaults (admin.tsx) — distinct from the
 * product stance bar, kept as-is visually, just centralized out of inline literals. */
export const ANNOUNCEMENT_BANNER_DEFAULT_BG = '#1E3A8A';
export const ANNOUNCEMENT_BANNER_DEFAULT_TEXT = '#FFFFFF';

/** Milestone confetti — product ramp + a fleck of gilt, the family's celebration
 * signature (Part 12). Used for real achievements only. */
export const MILESTONE_CONFETTI_COLORS = [
  SCG.key,
  SCG.lift,
  SCG.hover,
  GO.gilt,
  COLORS.white,
];

/** Canonical suite credits copy — Care-plan / agency billing stay out of this wallet. */
export const MESSAGING_CREDITS_COPY =
  'Messaging credits work across every SizzleGo product. One wallet powers your notifications, confirmations, and reminders in SignUpGo, SchoolConferenceGo, and EventTicketGo alike. A credit is $0.01. Email notifications are 1 credit. Text messages are 3 credits. Buy them once, spend them anywhere, and they never expire.';
